import React, { Fragment, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { modalOptions } from 'Helpers/modalOptions';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import {
    RISK_NODE_ITEMS_CHANGED,
    RiskNodeItemFields,
    riskNodeItemInitialValues,
    riskNodeItemPayload,
    riskNodeItemSchema,
} from 'CommonComponents/modals/RiskNodeItemFields';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

const ROOT_ID = 'ExpenseRiskOpportunityCreateModalApp';

const BaseSchema = Yup.object().shape({
    name: Yup.string().required($t('This value is required')),
    description: Yup.string(),
    AFC: Yup.bool(),
    amount: Yup.number().nullable().min(0),
});

const readNode = (el) => ({
    kind: el.getAttribute('data-node-kind'),
    id: el.getAttribute('data-node-id'),
    name: el.getAttribute('data-node-name'),
});

/**
 * Adds a risk or opportunity to a budget grid row's node. The node is fixed by
 * the row the modal was opened from.
 */
const ExpenseRiskOpportunityCreateModal = ({ storeRiskUrl, storeOpportunityUrl, impacts, probabilities }) => {
    const rootEl = document.getElementById(ROOT_ID);

    const [node, setNode] = useState(() => readNode(rootEl));
    const [type, setType] = useState(rootEl.getAttribute('data-type'));

    const initialValues = useMemo(
        () => ({
            name: '',
            description: '',
            amount: 0,
            AFC: false,
            ...riskNodeItemInitialValues(type),
        }),
        [type, node],
    );

    const validationSchema = useMemo(() => riskNodeItemSchema(type, BaseSchema), [type]);

    const toast = useToast();

    const modal = useModal({
        options: {
            ...modalOptions(true),
            backdrop: 'dynamic',
        },
    });

    // The grid opens the modal by writing the node, then the type.
    useEffect(() => {
        const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-type') {
                    setNode(readNode(mutation.target));
                    setType(mutation.target.getAttribute('data-type'));
                    modal.show();
                }
            }
        });

        observer.observe(rootEl, { attributes: true });

        return () => observer.disconnect();
    }, []);

    const modalTitle = useMemo(
        () => (type === 'risk' ? $t('Add Risk to {{name}}', { name: node.name }) : $t('Add Opportunity to {{name}}', { name: node.name })),
        [type, node],
    );

    const store = (values) => {
        const template = type === 'risk' ? storeRiskUrl : storeOpportunityUrl;
        const url = template.replace('NODE_KIND', node.kind).replace('NODE_ID', node.id);

        return axios.post(url, riskNodeItemPayload(type, values));
    };

    return (
        <Modal id="expense-risk-opportunity-create-modal" modal={modal} innerContainerClasses="tw-bg-[#F8F8F8]" parentContainerClasses="tw-max-w-lg">
            <ModalHeader title={modalTitle} close={() => modal.hide()}></ModalHeader>
            <div className="modal__form-container">
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                        store(values)
                            .then((res) => {
                                actions.resetForm();
                                modal.hide();

                                toast.success($t('The {{type}} has been successfully added.', { type }));

                                // The grid refreshes when the reporting job broadcasts; an open
                                // list modal re-fetches straight away.
                                dispatchEvent(new CustomEvent(RISK_NODE_ITEMS_CHANGED, { detail: res.data }));
                            })
                            .catch((e) => {
                                if (e?.response?.data?.errors) {
                                    actions.setErrors(e.response.data.errors);
                                } else {
                                    toast.error(e?.response?.data?.message || $t('An error occurred, please try again'));
                                }
                            })
                            .finally(() => {
                                actions.setSubmitting(false);
                            });
                    }}
                >
                    {({ values, isSubmitting, handleSubmit, setFieldValue }) => (
                        <Fragment>
                            <div>
                                <TextFormField
                                    label={$t('Name')}
                                    type="text"
                                    name="name"
                                    placeholder={type === 'risk' ? $t('Enter risk name') : $t('Enter opportunity name')}
                                />
                            </div>
                            <div>
                                <TextareaFormField
                                    label={$t('Description')}
                                    as="textarea"
                                    type="text"
                                    name="description"
                                    placeholder={type === 'risk' ? $t('Enter risk description') : $t('Enter opportunity description')}
                                />
                            </div>
                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <TextFormField label={$t('Amount')} type="number" name="amount"></TextFormField>
                            </div>
                            <RiskNodeItemFields type={type} impacts={impacts} probabilities={probabilities} />
                            <div>
                                <ToggleFormField
                                    label="AFC"
                                    type="checkbox"
                                    name="AFC"
                                    id="AFC"
                                    checked={values.AFC}
                                    onChange={() => {
                                        const newAfc = !values.AFC;
                                        setFieldValue('AFC', newAfc);
                                    }}
                                />
                            </div>
                            <ModalFooter
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Save')}
                                onCancel={() => modal.hide()}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                                submitButtonVariant={'primary'}
                            />
                        </Fragment>
                    )}
                </Formik>
            </div>
        </Modal>
    );
};

if (document.getElementById(ROOT_ID)) {
    const rootEl = document.getElementById(ROOT_ID);
    const parse = (name) => {
        try {
            return JSON.parse(rootEl.getAttribute(name) || '[]');
        } catch (e) {
            return [];
        }
    };

    ReactDOM.render(
        <App>
            <ExpenseRiskOpportunityCreateModal
                storeRiskUrl={rootEl.getAttribute('data-store-risk-url')}
                storeOpportunityUrl={rootEl.getAttribute('data-store-opportunity-url')}
                impacts={parse('data-impacts')}
                probabilities={parse('data-probabilities')}
            />
        </App>,
        rootEl,
    );
}
